import fs from 'fs/promises'
import path from 'path'
import os from 'os'

export interface Credentials {
  token: string
  apiUrl: string
  workspaceId?: string
  workspaceName?: string
  botId?: string
}

export interface Profile {
  name: string
  credentials: Credentials
  apiUrl: string
  lastUsed: string
  email?: string
  displayName?: string
  accountId?: string
  createdAt?: string
}

export interface ProfileMetadata {
  lastUsed: string
  accountId?: string
  email?: string
  displayName?: string
  createdAt?: string
}

export interface CredentialsStore {
  profiles: Record<string, Credentials>
  profileMetadata: Record<string, ProfileMetadata>
  currentProfile: string
}

export class CredentialsManager {
  private credentialsPath: string
  private configDir: string

  constructor() {
    // Store credentials in ~/.adk/credentials
    this.configDir = path.join(os.homedir(), '.adk')
    this.credentialsPath = path.join(this.configDir, 'credentials')
  }

  private async ensureConfigDir(): Promise<void> {
    try {
      await fs.mkdir(this.configDir, { recursive: true })
    } catch {
      // Directory might already exist, that's fine
    }
  }

  private async readCredentials(): Promise<CredentialsStore> {
    try {
      const data = await fs.readFile(this.credentialsPath, 'utf-8')
      return JSON.parse(data)
    } catch {
      // If file doesn't exist or is invalid, return empty store
      return {
        profiles: {},
        profileMetadata: {},
        currentProfile: 'default',
      }
    }
  }

  private async writeCredentials(store: CredentialsStore): Promise<void> {
    await this.ensureConfigDir()
    await fs.writeFile(this.credentialsPath, JSON.stringify(store, null, 2))
    // Set restrictive permissions (read/write for owner only)
    await fs.chmod(this.credentialsPath, 0o600)
  }

  async saveCredentials(
    profileName: string,
    credentials: Credentials,
    userInfo?: Partial<ProfileMetadata>
  ): Promise<void> {
    const store = await this.readCredentials()
    store.profiles[profileName] = credentials

    // Initialize metadata if it doesn't exist
    if (!store.profileMetadata) {
      store.profileMetadata = {}
    }

    // Update metadata with user info
    store.profileMetadata[profileName] = {
      lastUsed: new Date().toISOString(),
      ...userInfo,
    }

    // If this is the first profile, make it current
    if (Object.keys(store.profiles).length === 1) {
      store.currentProfile = profileName
    }

    await this.writeCredentials(store)
  }

  async getCredentials(profileName?: string): Promise<Credentials | null> {
    const store = await this.readCredentials()
    const profile = profileName || store.currentProfile

    if (!store.profiles[profile]) {
      return null
    }

    return store.profiles[profile]
  }

  async listProfiles(): Promise<Profile[]> {
    const store = await this.readCredentials()

    // Initialize metadata if it doesn't exist
    if (!store.profileMetadata) {
      store.profileMetadata = {}
    }

    return Object.entries(store.profiles).map(([name, credentials]) => {
      const metadata = store.profileMetadata[name] || ({} as ProfileMetadata)
      return {
        name,
        credentials,
        apiUrl: credentials.apiUrl,
        lastUsed: metadata.lastUsed || new Date().toISOString(),
        email: metadata.email,
        displayName: metadata.displayName,
        accountId: metadata.accountId,
        createdAt: metadata.createdAt,
      }
    })
  }

  async getCurrentProfile(): Promise<string> {
    const store = await this.readCredentials()
    return store.currentProfile || 'default'
  }

  async getCurrentProfileDetails(): Promise<Profile | null> {
    const store = await this.readCredentials()
    const currentProfileName = store.currentProfile || 'default'

    if (!store.profiles[currentProfileName]) {
      return null
    }

    const credentials = store.profiles[currentProfileName]
    const metadata = store.profileMetadata?.[currentProfileName] || ({} as ProfileMetadata)

    return {
      name: currentProfileName,
      credentials,
      apiUrl: credentials.apiUrl,
      lastUsed: metadata.lastUsed || new Date().toISOString(),
      email: metadata.email,
      displayName: metadata.displayName,
      accountId: metadata.accountId,
      createdAt: metadata.createdAt,
    }
  }

  async setCurrentProfile(profileName: string): Promise<void> {
    const store = await this.readCredentials()

    if (!store.profiles[profileName]) {
      throw new Error(`Profile '${profileName}' not found`)
    }

    // Initialize metadata if it doesn't exist
    if (!store.profileMetadata) {
      store.profileMetadata = {}
    }

    // Update last used time while preserving other metadata
    store.profileMetadata[profileName] = {
      ...store.profileMetadata[profileName], // Preserve existing metadata
      lastUsed: new Date().toISOString(),
    }

    store.currentProfile = profileName
    await this.writeCredentials(store)
  }

  async deleteProfile(profileName: string): Promise<void> {
    const store = await this.readCredentials()

    if (!store.profiles[profileName]) {
      throw new Error(`Profile '${profileName}' not found`)
    }

    delete store.profiles[profileName]

    // If we deleted the current profile, switch to another one
    if (store.currentProfile === profileName) {
      const remainingProfiles = Object.keys(store.profiles)
      store.currentProfile = remainingProfiles.length > 0 ? remainingProfiles[0]! : 'default'
    }

    await this.writeCredentials(store)
  }

  async getActiveCredentials(): Promise<Credentials> {
    // Check environment variable first
    const envProfile = process.env.ADK_PROFILE

    const credentials = await this.getCredentials(envProfile)

    if (!credentials) {
      const profileName = envProfile || 'default'
      throw new Error(`No credentials found for profile '${profileName}'. ` + `Please run 'adk login' to authenticate.`)
    }

    return credentials
  }
}
